/**
 * Social Media Sharing Utilities
 *
 * Helper functions for sharing content on various social media platforms
 */

export interface ShareData {
  url: string;
  title: string;
  text?: string;
}

/**
 * Share on Facebook
 */
export const shareToFacebook = (data: ShareData): void => {
  const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(data.url)}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
};

/**
 * Share on Twitter/X
 */
export const shareToTwitter = (data: ShareData): void => {
  const text = data.text || data.title;
  const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(data.url)}&text=${encodeURIComponent(text)}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
};

/**
 * Share on WhatsApp
 */
export const shareToWhatsApp = (data: ShareData): void => {
  const text = data.text || data.title;
  const message = `${text} ${data.url}`;
  const shareUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
};

/**
 * Share via Email
 */
export const shareViaEmail = (data: ShareData): void => {
  const subject = encodeURIComponent(data.title);
  const body = encodeURIComponent(`${data.text || 'Check out this meditation session'}\n\n${data.url}`);
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};

/**
 * Share on LinkedIn
 */
export const shareToLinkedIn = (data: ShareData): void => {
  const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(data.url)}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
};

/**
 * Share on Telegram
 */
export const shareToTelegram = (data: ShareData): void => {
  const text = data.text || data.title;
  const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(data.url)}&text=${encodeURIComponent(text)}`;
  window.open(shareUrl, '_blank', 'width=600,height=400');
};

/**
 * Copy link to clipboard with fallback
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    // Modern clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    return successful;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

/**
 * Copy link for Instagram (with instructions since Instagram doesn't support direct URL sharing)
 */
export const copyForInstagram = async (url: string): Promise<{ success: boolean; message: string }> => {
  const success = await copyToClipboard(url);

  if (success) {
    return {
      success: true,
      message: 'Link copied! Open Instagram and paste in your story, bio, or DM.'
    };
  }

  return {
    success: false,
    message: 'Failed to copy link. Please copy manually.'
  };
};

/**
 * Copy link for TikTok (with instructions since TikTok doesn't support direct URL sharing)
 */
export const copyForTikTok = async (url: string): Promise<{ success: boolean; message: string }> => {
  const success = await copyToClipboard(url);

  if (success) {
    return {
      success: true,
      message: 'Link copied! Open TikTok and paste in your bio or video description.'
    };
  }

  return {
    success: false,
    message: 'Failed to copy link. Please copy manually.'
  };
};

/**
 * Check if native Web Share API is available
 */
export const isNativeShareAvailable = (): boolean => {
  return typeof navigator !== 'undefined' && 'share' in navigator;
};

/**
 * Use native Web Share API if available
 */
export const nativeShare = async (data: ShareData): Promise<boolean> => {
  if (!isNativeShareAvailable()) {
    return false;
  }

  try {
    await navigator.share({
      title: data.title,
      text: data.text || data.title,
      url: data.url
    });
    return true;
  } catch (err) {
    // User cancelled or share failed
    console.error('Native share failed:', err);
    return false;
  }
};

/**
 * Get platform-specific share text
 */
export const getShareText = (title: string, platform?: string): string => {
  const baseText = `Listen to this meditation: ${title}`;

  switch (platform) {
    case 'twitter':
      return `${baseText} #Meditation #Mindfulness #VoiceThera`;
    case 'facebook':
      return `${baseText}`;
    case 'whatsapp':
      return `🧘 ${baseText}`;
    case 'instagram':
      return `${title} 🧘✨`;
    case 'tiktok':
      return `${title} #meditation #mindfulness`;
    default:
      return baseText;
  }
};
